/**
 * Lazy-loads the COUNT + first-N rows for a single inbound-FK relation.
 *
 * The query is gated by `enabled` (typically `expanded`-state from the
 * Related Data Panel) so collapsed sections don't fire any queries. Cache
 * key includes engine + connection + db + schema + table + pkHash +
 * relationKey so re-expanding within the same row visit is a cache hit.
 *
 * Concurrency cap during "Expand all" is enforced by the panel
 * (`RelatedDataPanel`), not here — this hook trusts its caller.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createExecutionId } from '../../../../types/queryExecution';
import { fetchTableExplorerRows } from '../../table-explorer/services/tableExplorerApi';
import { buildInboundForeignKeyFilters } from '../../table-explorer/utils/inboundForeignKeyUtils';
import { getEngineForConnection } from '../../../connections/utils/connectionLookup';
import type { TableExplorerColumn } from '../../core/types/workspaceTypes';
import type { InboundRelation } from '../types/singleRowTypes';

export type UseInboundRelationDataParams = {
  readonly connectionId: string;
  readonly database: string;
  readonly relation: InboundRelation;
  readonly parentRow: Record<string, unknown> | null;
  readonly enabled: boolean;
  readonly limit?: number;
};

export type InboundRelationDataResult = {
  readonly rows: ReadonlyArray<Record<string, unknown>>;
  readonly columns: ReadonlyArray<TableExplorerColumn>;
  readonly totalRowCount?: number;
};

function hashParentRowByPk(
  parentRow: Record<string, unknown> | null,
  toColumn: string,
): string {
  if (!parentRow) return 'null';
  return `${toColumn}=${JSON.stringify(parentRow[toColumn])}`;
}

export function useInboundRelationData(params: UseInboundRelationDataParams): {
  readonly data: InboundRelationDataResult | undefined;
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly error: unknown;
  readonly refetch: () => void;
} {
  const { connectionId, database, relation, parentRow, enabled, limit = 3 } = params;

  const filterBuild = useMemo(() => {
    if (!parentRow) return { filters: null };
    return buildInboundForeignKeyFilters({
      foreignKey: relation.source,
      parentRow,
      looseRel: undefined,
    });
  }, [relation.source, parentRow]);

  const pkHash = useMemo(
    () => hashParentRowByPk(parentRow, relation.toColumn),
    [parentRow, relation.toColumn],
  );

  const isEnabled = enabled && !!connectionId && !!parentRow && !!filterBuild.filters;

  const engine = connectionId ? getEngineForConnection(connectionId) : 'unknown';

  const query = useQuery<InboundRelationDataResult>({
    queryKey: [
      'inbound-relation',
      engine,
      connectionId,
      database,
      relation.fromSchema,
      relation.fromTable,
      pkHash,
      relation.relationKey,
      limit,
    ],
    queryFn: async () => {
      if (!filterBuild.filters) {
        return { rows: [], columns: [] };
      }
      const executionId = createExecutionId(`inbound-relation-${relation.fromTable}`);
      const result = await fetchTableExplorerRows({
        connectionId,
        executionId,
        table: {
          database,
          schema: relation.fromSchema,
          tableName: relation.fromTable,
        },
        columns: '*',
        limit,
        filters: filterBuild.filters,
        sorting: [],
      });
      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch related rows');
      }
      return {
        rows: result.rows || [],
        columns: result.columns || [],
        totalRowCount: result.totalRowCount,
      };
    },
    enabled: isEnabled,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}
